import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, Gift, ShoppingBag, KeyRound } from "lucide-react";
import { MaskedLine, FadeUp, Eyebrow } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import ProductCard from "@/components/ProductCard";
import ProductModal from "@/components/ProductModal";
import { IMAGES, PRODUCTS } from "@/data/products";

const STEPS = [
  { n: "01", title: "Tell us about them", text: "Their quirks, passions, the occasion, your budget — by form, phone or a simple message." },
  { n: "02", title: "We compose the gift", text: "Our curators personally source, assemble and propose. The price is agreed with you before anything is paid." },
  { n: "03", title: "Wrapped & delivered", text: "Hand-wrapped in our signature ivory and gold, with a handwritten note — delivered to their door." },
];

export default function Home() {
  const heroRef = useRef(null);
  const [activeProduct, setActiveProduct] = useState(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden spotlight pt-32 pb-24">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-6 relative z-10">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}>
              <Eyebrow>Personalised Luxury Gifting &amp; Concierge</Eyebrow>
            </motion.div>
            <h1 className="font-serif font-light text-chocolate text-4xl sm:text-5xl lg:text-[4.2rem] leading-[1.05] tracking-tight mt-7">
              <MaskedLine delay={0.15}>You tell us</MaskedLine>
              <MaskedLine delay={0.3}>about them.</MaskedLine>
              <MaskedLine delay={0.5} className="italic text-golddark mt-1">We create the gift.</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.75, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="text-base md:text-lg text-chocolate/65 leading-relaxed max-w-md mt-8"
            >
              Bespoke gift curation, a ready-to-gift collection, and a private shopping concierge — for the people who deserve more than ordinary.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="flex flex-wrap gap-4 mt-10"
            >
              <Link
                to="/gift-edit#brief"
                data-testid="hero-create-gift-button"
                className="group relative overflow-hidden inline-flex items-center gap-2 bg-chocolate text-cream px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-espresso transition-colors duration-300"
              >
                Create a Gift Edit
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/concierge"
                data-testid="hero-concierge-button"
                className="group inline-flex items-center gap-2 border border-gold/70 text-chocolate px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-gold/10 hover:border-gold transition-all duration-300"
              >
                Private Concierge
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.15, duration: 0.8 }}
              className="font-mono text-[10px] uppercase tracking-[0.25em] text-chocolate/45 mt-10"
            >
              Personally sourced · Hand-wrapped · No fixed prices — agreed with you first
            </motion.p>
          </div>

          <div className="lg:col-span-6 relative hidden md:block" aria-hidden="true">
            <div className="absolute -inset-10 spotlight" />
            <motion.div
              style={{ y: yMain }}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="relative ml-auto w-[78%] aspect-[3/4] overflow-hidden frame-gold"
            >
              <img src={IMAGES.heroMain} alt="" className="img-warm w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-espresso/25 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              style={{ y: yCard }}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 1, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -left-2 bottom-6 w-52 aspect-[4/5] overflow-hidden frame-gold animate-float-slow"
            >
              <img src={IMAGES.packaging} alt="" className="img-warm w-full h-full object-cover" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.05, duration: 0.9 }}
              className="absolute -right-3 top-8 bg-cream/95 backdrop-blur border border-gold/40 px-5 py-4"
            >
              <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-golddark">Signature wrap</p>
              <p className="font-serif italic text-lg text-chocolate mt-1">Ivory, sage &amp; gold</p>
            </motion.div>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2"
        >
          <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-chocolate/40">Scroll</span>
          <span className="w-px h-10 bg-gradient-to-b from-gold to-transparent" />
        </motion.div>
      </section>

      <Marquee />

      {/* THREE WAYS BENTO */}
      <section className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <FadeUp>
          <Eyebrow>Three ways to give</Eyebrow>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-chocolate mt-5 max-w-xl leading-[1.15]">
            However you gift, <span className="italic text-golddark">we make it personal.</span>
          </h2>
        </FadeUp>

        <div className="grid lg:grid-cols-12 gap-6 mt-14">
          <FadeUp className="lg:col-span-7">
            <Link to="/gift-edit" data-testid="service-card-bespoke" className="group relative block h-[420px] lg:h-full lg:min-h-[520px] overflow-hidden">
              <img src={IMAGES.bespoke} alt="A bespoke gift being assembled" loading="lazy"
                className="img-warm absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.05]" />
              <div className="absolute inset-0 bg-gradient-to-t from-espresso/85 via-espresso/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8 lg:p-10">
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-champagne mb-3">01 — Bespoke</p>
                <h3 className="font-serif text-3xl lg:text-4xl text-cream font-light">The Gift Edit</h3>
                <p className="text-cream/70 text-sm mt-3 max-w-md leading-relaxed">
                  Tell us about the recipient and we personally source, curate, assemble and hand-wrap a gift that could only be for them.
                </p>
                <span className="inline-flex items-center gap-2 mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-champagne border-b border-champagne/40 pb-1 group-hover:gap-3.5 transition-all">
                  Begin a bespoke edit <ArrowRight size={13} />
                </span>
              </div>
            </Link>
          </FadeUp>

          <div className="lg:col-span-5 grid grid-rows-2 gap-6">
            <FadeUp delay={0.1} className="h-full">
              <Link to="/collection" data-testid="service-card-collection"
                className="group h-full min-h-[240px] bg-ivory border border-chocolate/10 hover:border-gold/60 p-8 lg:p-10 flex flex-col justify-between transition-colors duration-500">
                <div className="flex items-start justify-between">
                  <ShoppingBag size={26} className="text-golddark" strokeWidth={1.5} />
                  <ArrowUpRight size={18} className="text-chocolate/30 group-hover:text-golddark group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-golddark mb-2">02 — Ready-made</p>
                  <h3 className="font-serif text-2xl lg:text-3xl text-chocolate font-light">The Collection</h3>
                  <p className="text-chocolate/60 text-sm mt-2 leading-relaxed">
                    Pre-made, pre-bought and ready to gift — browse edits, little gifts and seasonal boxes.
                  </p>
                </div>
              </Link>
            </FadeUp>
            <FadeUp delay={0.2} className="h-full">
              <Link to="/concierge" data-testid="service-card-concierge"
                className="group h-full min-h-[240px] bg-espresso p-8 lg:p-10 flex flex-col justify-between relative overflow-hidden">
                <div className="absolute inset-0 spotlight-dark" />
                <div className="relative flex items-start justify-between">
                  <KeyRound size={26} className="text-champagne" strokeWidth={1.5} />
                  <ArrowUpRight size={18} className="text-cream/30 group-hover:text-champagne group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                </div>
                <div className="relative">
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-champagne mb-2">03 — Concierge</p>
                  <h3 className="font-serif text-2xl lg:text-3xl text-cream font-light">Private Concierge</h3>
                  <p className="text-cream/60 text-sm mt-2 leading-relaxed">
                    High-value, rare or urgently needed — brief us privately and we handle the rest.
                  </p>
                </div>
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-28 bg-ivory border-y border-chocolate/10">
        <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
          <FadeUp className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <Eyebrow>How the Gift Edit works</Eyebrow>
              <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-chocolate mt-5 leading-[1.15]">
                Three steps. <span className="italic text-golddark">Zero guesswork.</span>
              </h2>
            </div>
            <Link to="/gift-edit" data-testid="how-it-works-cta"
              className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-golddark border-b border-gold/50 pb-1 hover:text-chocolate transition-colors shrink-0">
              Explore The Gift Edit <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </FadeUp>
          <div className="grid md:grid-cols-3 gap-10 mt-16">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.12}>
                <div data-testid={`home-step-${i + 1}`} className="border-t-2 border-gold/60 pt-8">
                  <span className="font-serif text-6xl font-light text-gold/60">{s.n}</span>
                  <h3 className="font-serif text-2xl text-chocolate mt-5">{s.title}</h3>
                  <p className="text-sm text-chocolate/60 mt-3 leading-relaxed">{s.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      {/* COLLECTION SPOTLIGHT */}
      <section className="py-20 lg:py-32 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <FadeUp className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <Eyebrow>The Collection</Eyebrow>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light text-chocolate mt-5 leading-[1.15]">
              Ready to gift, <span className="italic text-golddark">ready to go.</span>
            </h2>
          </div>
          <Link to="/collection" data-testid="collection-spotlight-cta"
            className="group inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-golddark border-b border-gold/50 pb-1 hover:text-chocolate transition-colors shrink-0">
            Browse The Collection <ArrowRight size={13} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </FadeUp>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PRODUCTS.filter((p) => p.featured).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} onEnquire={setActiveProduct} />
          ))}
        </div>
      </section>

      {/* CONCIERGE TEASER */}
      <section className="relative bg-espresso py-24 lg:py-36 overflow-hidden">
        <div className="absolute inset-0 spotlight-dark" />
        <div className="relative px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <FadeUp>
              <Eyebrow dark>Private Shopping &amp; Gifting Concierge</Eyebrow>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-light text-cream leading-[1.12] mt-6">
                You tell us what you need. <span className="italic text-champagne">We handle the rest.</span>
              </h2>
              <p className="text-cream/60 text-base leading-relaxed mt-6 max-w-lg">
                A discreet service for high-value gifts, executive and corporate orders, event goodie bags, rare finds and the genuinely last-minute. We explore legitimate sourcing options, availability and timescales — and respond with an honest proposal.
              </p>
              <Link to="/concierge" data-testid="concierge-teaser-cta"
                className="group inline-flex items-center gap-2 mt-10 bg-gold text-espresso px-8 py-4 font-mono text-[11px] uppercase tracking-[0.2em] hover:bg-champagne transition-colors duration-300">
                Speak to the Concierge
                <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </FadeUp>
          </div>
          <div className="lg:col-span-5">
            <FadeUp delay={0.15}>
              <ul className="divide-y divide-white/10 border-y border-white/10">
                {["Luxury & high-value gifts", "Executive & corporate gifting", "Event & party goodie bags", "Last-minute & urgent sourcing", "Rare & hard-to-find items"].map((s) => (
                  <li key={s} className="py-4 flex items-center gap-4">
                    <span className="w-1.5 h-1.5 rotate-45 bg-gold shrink-0" />
                    <span className="font-serif text-lg text-cream/85 font-light">{s}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="py-24 lg:py-36 px-4 text-center relative">
        <FadeUp className="max-w-3xl mx-auto">
          <Gift size={22} className="text-gold mx-auto mb-8" strokeWidth={1.25} />
          <blockquote data-testid="home-quote" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-light italic text-chocolate leading-[1.35]">
            "The best gifts are never simply found. They are composed — around a person, a moment, a memory."
          </blockquote>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-golddark mt-8">Salem &amp; Sabrina — Founders</p>
        </FadeUp>
      </section>

      <ProductModal product={activeProduct} onClose={() => setActiveProduct(null)} />
    </div>
  );
}
